import { useMsal } from '@azure/msal-react';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, makeStyles, mergeClasses, Option, Radio, RadioGroup, Spinner } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { User } from '../../../models';
import { NominationForStaffRouteParams } from '../../../models/route-params';
import { SelectOptions } from '../../shared-components/form';
import './AssignJudgeModal.scss';

interface AssignJudgeModalProps {
  onCancel(): void;
  onSave(selectedLead: User): void;
  onSkipToNextJudge(): void;
  isNominationHasJudge: boolean;
}

const useStyles = makeStyles({
  surface: { maxWidth: '416px' }
});

const AssignJudgeModalImpl: React.FC<AssignJudgeModalProps> = (props) => {
  const {
    onCancel,
    onSave,
    onSkipToNextJudge,
    isNominationHasJudge,
  } = props;

  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    nominationForStaffPageUiStore,
  } = useStores();

  const {
    awardId,
    countryId,
  } = useParams<NominationForStaffRouteParams>();

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const [selectedJudge, setSelectedJudge] = useState<User | undefined>(undefined);
  const [isSkipToNextJudge, setSkipToNextJudge] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(true);

  const loadAvailableJudges = async (): Promise<void> => {
    setLoading(true);
    try {
      const currentCountryId = countryId ? Number(countryId) : undefined;
      const judges = await apiStore.getAvailableJudges(
        instance,
        Number(awardId),
        nominationForStaffPageUiStore.nominationForStaff.nominationId,
        true,
        currentCountryId,
      );
      nominationForStaffPageUiStore.setAvailableJudges(judges);
    } catch (error) {
      // TODO - handle error
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAvailableJudges();
  }, []);

  const handleSaveButtonClicked = (): void => {
    if (isSkipToNextJudge) {
      onSkipToNextJudge();
      return;
    }
    if (!selectedJudge) return;

    onSave(selectedJudge);
  }

  const handleJudgeSelected = (emailAddress: string): void => {
    setSelectedJudge(nominationForStaffPageUiStore.getAvailableJudgeByEmail(emailAddress));
  }

  return (
    <Dialog
      open
      onOpenChange={(_, data) => {
        if (!data.open) onCancel();
      }}
    >
      <DialogSurface className={mergeClasses('assign-judge-modal', commonStyles.modalSurface, styles.surface)}>
        <DialogBody className={commonStyles.modalBody}>
          <DialogTitle
            action={<Button
              appearance='transparent'
              title='Close'
              icon={<DismissRegular />}
              onClick={onCancel}
            />}
          >
            {isNominationHasJudge ? 'Skip to next judge' : 'Assign judge'}
          </DialogTitle>
          <DialogContent>
            {
              isNominationHasJudge &&
              <RadioGroup>
                <Radio
                  checked={isSkipToNextJudge}
                  label='Skip to next judge in pool'
                  onChange={(event) => setSkipToNextJudge(event.target.checked)}
                />
                <Radio
                  checked={!isSkipToNextJudge}
                  label='Assign to judge'
                  onChange={(event) => setSkipToNextJudge(!event.target.checked)}
                />
              </RadioGroup>
            }
            <SelectOptions
              label='Select judge'
              onOptionSelect={(data) => handleJudgeSelected(data.optionValue ?? '')}
              dropdownProps={{ value: selectedJudge?.emailAddress }}
            >
              {
                isLoading ?
                  <Spinner /> :
                  nominationForStaffPageUiStore.availableJudges.map((judge) => {
                    return (
                      <Option
                        key={judge.emailAddress}
                        value={judge.emailAddress}
                      >
                        {judge.userId ? `${judge.firstName} ${judge.lastName}` : judge.emailAddress}
                      </Option>
                    );
                  })
              }
            </SelectOptions>
          </DialogContent>
          <DialogActions>
            <Button
              appearance='secondary'
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              appearance='primary'
              onClick={handleSaveButtonClicked}
              disabled={!isSkipToNextJudge && !selectedJudge}
            >
              Save
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export const AssignJudgeModal = observer(AssignJudgeModalImpl);
